import { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { ladderWith, servingsWord } from './servings';

export type AddChoice = { count: number; servings: number; wants: string };

/** More dinners for a week that has already been bought.
 *
 * This is what "redo this week" becomes once the shopping is put away. The
 * week cannot be re-chosen then -- the food is in the cupboard -- but running
 * through three dinners by Wednesday and wanting a fourth before Sunday is
 * ordinary, and the only thing the page could previously offer was throwing
 * the whole week away.
 *
 * All three answers reach the model. The count and the servings because they
 * decide what gets bought, and the free text because a shortlist ordered by
 * season and sales cannot know that what is wanted tonight is something
 * quick. It stays optional: a cook who just wants one more dinner should not
 * have to explain themselves to get it.
 */
export default function AddRecipesButton({
  storeName,
  defaultServings,
  pending,
  error,
  onAdd,
}: {
  storeName: string;
  /** The household's usual figure — the modal's default, not the week's. */
  defaultServings: number;
  pending: boolean;
  /** Shown inside the dialog, because that is where it was asked for. */
  error?: string;
  onAdd: (choice: AddChoice, done: () => void) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [count, setCount] = useState(1);
  const [servings, setServings] = useState(defaultServings);
  const [wants, setWants] = useState('');

  // Backing out clears the answers, as swapping does. A half-typed request
  // surviving until next week would attach it to the wrong plan.
  const close = () => {
    setAsking(false);
    setCount(1);
    setServings(defaultServings);
    setWants('');
  };

  return (
    <>
      <button
        className="btn btn--wide"
        disabled={pending}
        onClick={() => setAsking(true)}
      >
        Add more recipes
      </button>

      {asking && (
        <ConfirmDialog
          titleId="add-recipes-title"
          title="Add more dinners"
          onCancel={close}
          actions={
            <>
              <button
                className="btn btn--primary"
                disabled={pending}
                onClick={() =>
                  onAdd({ count, servings, wants: wants.trim() }, close)
                }
              >
                {pending
                  ? 'Choosing…'
                  : `Add ${count} dinner${count === 1 ? '' : 's'}`}
              </button>
              <button className="link-button" onClick={close}>
                Never mind
              </button>
            </>
          }
        >
          <label className="field">
            <span>How many more dinners?</span>
            <select
              value={count}
              onChange={(event) => setCount(Number(event.target.value))}
            >
              {[1, 2, 3].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>For how many?</span>
            <select
              value={servings}
              onChange={(event) => setServings(Number(event.target.value))}
            >
              {ladderWith(defaultServings).map((option) => (
                <option key={option} value={option}>
                  {servingsWord(option)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>What would you like?</span>
            <textarea
              rows={3}
              value={wants}
              onChange={(event) => setWants(event.target.value)}
              placeholder="Optional — something quick, or using up the chicken."
            />
          </label>

          {/* The two things that are not obvious: these are chosen from what
              {storeName} stocks, and they are not paid for yet. */}
          <p className="muted small">
            Chosen from what {storeName} has, and from what’s left in your
            pantry. They go on the end of this week, and your shopping list
            grows by what they need.
          </p>

          {/* Kept in the dialog rather than behind it: a refusal is an answer
              to the question still on screen. */}
          {error && <p className="error">{error}</p>}
        </ConfirmDialog>
      )}
    </>
  );
}
